import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useMemo } from 'react';
import { usePlayerChart } from '../context/usePlayerChart';

Highcharts.setOptions({
	time: {
		useUTC: false,
	},
});

const PlayerChart = (props: { highChartHeight?: number; tooltipPositionTop?: boolean; yAxisTitle?: string }) => {
	const { series, currentTimestamp, minTimestamp, maxTimestamp, qualityMap, requestEvents } = usePlayerChart();

	const dateTimeFormat = '%Y-%m-%d %I:%M:%S %p';

	const getColorString = useCallback((color?: Highcharts.ColorType): string => {
		if (typeof color === 'string') {
			return color;
		}
		return '#000000';
	}, []);

	const hexToRgb = useCallback((hex: string) => {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result ? {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16)
		} : { r: 0, g: 0, b: 0 };
	}, []);

	const tickIntervalMs = useMemo(() => {
		const timeRangeMs = maxTimestamp - minTimestamp;
		const targetTicks = 10;
		const calculatedInterval = Math.ceil(timeRangeMs / targetTicks);

		return Math.max(
			15 * 60 * 1000,
			Math.ceil(calculatedInterval / (15 * 60 * 1000)) * (15 * 60 * 1000),
		);
	}, [maxTimestamp, minTimestamp]);

	const tooltipFormatter = useCallback(function (this: Highcharts.TooltipFormatterContextObject) {
		let tooltip =
			'<b>' + Highcharts.dateFormat(dateTimeFormat, this.x as number) + '</b><br/><br/>';
		this.points?.forEach((point, index) => {
			const colorValue = getColorString(point.color ?? point.series.color);
			const seriesOptions = point.series.options as Highcharts.SeriesOptionsType & {
				additionalInfo?: Array<{ label: string; value: string | number }>;
				valueUnit?: string;
			};
			const valueUnit = seriesOptions.valueUnit ? ` ${seriesOptions.valueUnit}` : '';
			tooltip +=
				'<span style="color:' +
				colorValue +
				'">● ' +
				point.series.name +
				': </span><b>' +
				point.y +
				valueUnit +
				'</b>';

			if (seriesOptions.additionalInfo && Array.isArray(seriesOptions.additionalInfo)) {
				seriesOptions.additionalInfo.forEach(
					(info: { label: string; value: string | number }) => {
						tooltip += '<br/><span>' + info.label + ': </span><b>' + info.value + '</b>';
					},
				);
			}

			const seriesQualityMap = qualityMap.get(point.series.name);
			const quality = seriesQualityMap?.get(this.x as number);
			if (quality !== undefined && quality !== null) {
				tooltip += '<br/><span>Quality: </span><b>' + quality + '</b>';
			}

			if (index < (this.points?.length ?? 0) - 1) {
				tooltip += '<br/><br/>';
			}
		});
		return tooltip;
	}, [dateTimeFormat, getColorString, qualityMap]);

	const chartOptions: Highcharts.Options = useMemo(() => ({
		accessibility: {
			enabled: false,
		},
		chart: {
			height: props.highChartHeight ?? 'auto',
			marginLeft: 85,
			marginRight: 55,
			spacingTop: 5,
			spacingBottom: 0,
			style: {
				overflow: 'visible',
			},
		},
		exporting: {
			enabled: false,
		},
		title: {
			text: undefined,
		},
		xAxis: {
			type: 'datetime',
			min: minTimestamp,
			max: maxTimestamp,
			tickInterval: tickIntervalMs,
			dateTimeLabelFormats: {
				hour: '%I:%M %p',
			},
			plotLines: [
				...(currentTimestamp
					? [
							{
								color: '#22a2ca',
								width: 2,
								value: currentTimestamp,
								dashStyle: 'Solid' as const,
								zIndex: 5,
							},
						]
					: []),
				...requestEvents
					.filter((event) => currentTimestamp !== null && event.timestamp <= currentTimestamp)
					.map((event) => ({
						color: '#00203C',
						width: 2,
						value: event.timestamp,
						dashStyle: 'Solid' as const,
						zIndex: 3,
					})),
			],
		},
		yAxis: {
			title: {
				text: props.yAxisTitle ?? '',
			},
			labels: {
				reserveSpace: true,
			},
			gridLineWidth: 1,
			gridLineColor: '#e6e6e6',
		},
		series: series,
		tooltip: {
			shared: true,
			xDateFormat: dateTimeFormat,
			outside: true,
			style: {
				zIndex: 99999,
			},
			className: 'highcharts-tooltip-custom',
			positioner: props.tooltipPositionTop
				? function (labelWidth, labelHeight, point) {
						const chartX = point.plotX + this.chart.plotLeft;
						const chartY = point.plotY + this.chart.plotTop;

						let x = chartX - labelWidth / 2;
						const y = chartY - labelHeight - 10;

						if (x < 0) {
							x = 10;
						}
						const maxX = this.chart.chartWidth - labelWidth - 10;
						if (x > maxX) {
							x = maxX;
						}

						return { x, y };
					}
				: undefined,
			formatter: tooltipFormatter,
		},
		legend: {
			enabled: true,
			margin: 5,
			itemMarginBottom: 2,
			useHTML: true,
			symbolWidth: 0,
			symbolHeight: 0,
			symbolRadius: 0,
			align: 'center',
			itemStyle: {
				textAlign: 'center'
			},
			labelFormatter: function () {
				const colorString = getColorString(this.color);
				let color = hexToRgb(colorString);
				if (!this.visible) {
					color = { r: 204, g: 204, b: 204 };
				}
				const symbol = `<span class="chartSymbol" style="display: inline-block; width: 12px; height: 12px; background-color: rgba(${color.r},${color.g},${color.b},1); border: 2px solid rgba(${color.r},${color.g},${color.b},.8); border-radius: 2px; margin-right: 4px; vertical-align: middle; position: relative; color: white; font-size: 8px; font-weight: bold; text-align: center; line-height: 8px;">✓</span>`;
				return `<div style="display: flex; align-items: center; justify-content: center;">${symbol} <span>${this.name}</span></div>`;
			},
		},
		plotOptions: {
			line: {
				marker: {
					enabled: false,
					states: {
						hover: {
							enabled: true,
							radius: 4,
						}
					}
				},
				connectNulls: true,
			},
			series: {
				connectNulls: true,
				states: {
					hover: {
						enabled: true,
						lineWidthPlus: 1,
					},
				},
			},
		},
	}), [
		props.highChartHeight, 
		minTimestamp, 
		maxTimestamp, 
		tickIntervalMs, 
		currentTimestamp, 
		series, 
		dateTimeFormat, 
		props.tooltipPositionTop,
		props.yAxisTitle,
		tooltipFormatter, 
		getColorString,
		hexToRgb,
		requestEvents
	]);

	return (
		<div className="px-6" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
			<HighchartsReact highcharts={Highcharts} options={chartOptions} />
		</div>
	);
};

export default PlayerChart;
